/**
 * Rúbricas de calificación.
 *
 * ⚠️ Nombre LEGACY: la entidad se llama `Escala` desde 2026-07 y las rutas son
 * `/api/escalas`. El JSON de la rúbrica es polimórfico según el tipo de
 * información: documental, cuantitativa y opinión tienen formas distintas.
 *
 * Store Pinia: estado compartido del módulo y orquestación de sus servicios.
 * Las vistas no llaman a `http` ni a los servicios directamente; pasan por aquí.
 * Los servicios no lanzan, así que las acciones interpretan valores neutros
 * (`[]`, `null`, `false`) como fallo.
 */

import { defineStore } from 'pinia'
import type { CriterioEvaluacion } from '@/interfaces/CriterioEvaluacion'
import {getCriteriosEvaluacionService, getCriterioEvaluacionByIdService, crearCriterioEvaluacionService, updateCriterioEvaluacionService, eliminarCriterioEvaluacionService} from '@/services/CriterioEvaluacionService'

/** Store `criterioEvaluacion`. Ver la cabecera del archivo para el contrato del módulo. */
export const useGestionCriterioEvaluacion = defineStore('criterioEvaluacion', {
  state: () => ({
    criteriosEvaluacion: [] as CriterioEvaluacion[],
    criterioActual: null as CriterioEvaluacion | null
  }),

  actions: {
    async cargarTodos(tipoInformacion?: string): Promise<void> {
      const lista = await getCriteriosEvaluacionService(tipoInformacion)
      this.criteriosEvaluacion = Array.isArray(lista) ? lista : []
    },

    async cargarPorId(id: number): Promise<CriterioEvaluacion | null> {
      const criterio = await getCriterioEvaluacionByIdService(id)
      this.criterioActual = criterio
      return criterio
    },

    async crear(
      data: Omit<CriterioEvaluacion, 'id'>
    ): Promise<{ ok: boolean; data?: CriterioEvaluacion; errors?: Record<string, string[]> }> {
      const result = await crearCriterioEvaluacionService(data)
      if (result.ok && result.data) {
        this.criteriosEvaluacion.push(result.data)
        this.criterioActual = result.data
      }
      return result
    },

    async actualizar(
      id: number,
      payload: Partial<CriterioEvaluacion>
    ): Promise<{ ok: boolean; data?: CriterioEvaluacion; errors?: Record<string, string[]> }> {
      const result = await updateCriterioEvaluacionService(id, payload)
      if (result.ok && result.data) {
        const idx = this.criteriosEvaluacion.findIndex(c => c.id === id)
        if (idx !== -1) this.criteriosEvaluacion.splice(idx, 1, result.data)
        this.criterioActual = result.data
      }
      return result
    },

    async eliminar(id: number): Promise<{ ok: boolean; errors?: Record<string, string[]> }> {
      const result = await eliminarCriterioEvaluacionService(id)
      if (result.ok) {
        this.criteriosEvaluacion = this.criteriosEvaluacion.filter(c => c.id !== id)
        if (this.criterioActual?.id === id) this.criterioActual = null
      }
      return result
    }
  }
})
